import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  ArrowsRightLeftIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  ArrowPathIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid,
  TruckIcon as TruckIconSolid,
  ArrowsRightLeftIcon as ArrowsRightLeftIconSolid
} from '@heroicons/react/24/solid';
import { getImageUrl } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

// استيراد الخدمات الجديدة
import { exchangeTransactionsAPI } from '../../config/api';

const ActiveExchanges = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const params = {
        status: filters.status === 'all' ? undefined : filters.status,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      const response = await exchangeTransactionsAPI.getMyTransactions(params);
      const transactionsData = response.data?.data?.transactions || [];
      setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
      if (error.message && !error.message.includes('404')) {
        toast.error('فشل في تحميل التبادلات النشطة');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (transactionId) => {
    try {
      const response = await exchangeTransactionsAPI.getTransaction(transactionId);
      setSelectedTransaction(response.data?.data || response.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error('فشل في تحميل تفاصيل التبادل');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { 
        icon: CheckCircleIcon, 
        text: 'مؤكد', 
        color: 'bg-green-100 text-green-800' 
      },
      shipping_a_to_b: { 
        icon: TruckIcon, 
        text: 'شحن A→B', 
        color: 'bg-blue-100 text-blue-800' 
      },
      shipping_b_to_a: { 
        icon: TruckIcon, 
        text: 'شحن B→A', 
        color: 'bg-blue-100 text-blue-800' 
      },
      both_shipping: { 
        icon: TruckIcon, 
        text: 'شحن مزدوج', 
        color: 'bg-indigo-100 text-indigo-800' 
      },
      completed: { 
        icon: CheckCircleIconSolid, 
        text: 'مكتمل', 
        color: 'bg-emerald-100 text-emerald-800' 
      },
      cancelled: { 
        icon: XCircleIcon, 
        text: 'ملغي', 
        color: 'bg-red-100 text-red-800' 
      }
    };
    
    const config = statusConfig[status] || statusConfig.confirmed;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-4 h-4 ml-1" />
        {config.text}
      </span>
    );
  };

  const getShippingStatusBadge = (status) => {
    const statusConfig = {
      pending: { text: 'في الانتظار', color: 'bg-gray-100 text-gray-800' },
      picked_up: { text: 'تم الاستلام', color: 'bg-blue-100 text-blue-800' },
      in_transit: { text: 'في الطريق', color: 'bg-yellow-100 text-yellow-800' },
      delivered: { text: 'تم التسليم', color: 'bg-green-100 text-green-800' },
      failed: { text: 'فشل', color: 'bg-red-100 text-red-800' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getLabDisplayName = (transaction, labKey) => {
    const labName = transaction.parties?.[labKey]?.lab?.labName || 'غير محدد';
    if (transaction.myRole === labKey) {
      return `${labName} (مختبرك)`;
    }
    return `مختبر ${labKey === 'labA' ? 'A' : 'B'}`;
  };

  const renderShippingCard = (transaction, direction, title) => {
    const shipping = transaction.shipping?.[direction];
    if (!shipping) return null;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">{title}</h4>
          {getShippingStatusBadge(shipping.status)}
        </div>

        <div className="space-y-2 text-sm text-gray-600 mb-4">
          {shipping.carrier && (
            <p><span className="font-medium">شركة الشحن:</span> {shipping.carrier}</p>
          )}
          {shipping.trackingNumber && (
            <p><span className="font-medium">رقم التتبع:</span> {shipping.trackingNumber}</p>
          )}
          {shipping.estimatedDelivery && (
            <p><span className="font-medium">التسليم المتوقع:</span> {new Date(shipping.estimatedDelivery).toLocaleDateString('ar-SA')}</p>
          )}
          {shipping.pickedUpAt && (
            <p><span className="font-medium">تاريخ الاستلام:</span> {new Date(shipping.pickedUpAt).toLocaleDateString('ar-SA')}</p>
          )}
          {shipping.deliveredAt && (
            <p><span className="font-medium">تاريخ التسليم:</span> {new Date(shipping.deliveredAt).toLocaleDateString('ar-SA')}</p>
          )}
        </div>
      </div>
    );
  };

  const renderTransactionCard = (transaction) => (
    <motion.div
      key={transaction._id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200"
    >
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-brand-blue to-brand-blueSecondary rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ArrowsRightLeftIconSolid className="h-6 w-6 text-white ml-2" />
            <h3 className="text-lg font-semibold text-white">معاملة تبادل #{transaction.transactionNumber}</h3>
          </div>
          {getStatusBadge(transaction.status)}
        </div>
      </div>
      <div className="p-6">

        {/* معلومات الأطراف */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <BuildingStorefrontIcon className="w-5 h-5 ml-2 text-blue-600" />
              {getLabDisplayName(transaction, 'labA')}
            </h4>
            <div className="space-y-2">
              <div>
                <p className="font-medium text-sm">المنتج: {transaction.parties?.labA?.product?.name || transaction.parties?.labA?.customProductName || 'عرض مخصص'}</p>
                <p className="text-xs text-gray-600">الكمية: {transaction.parties?.labA?.quantity}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <BuildingStorefrontIcon className="w-5 h-5 ml-2 text-green-600" />
              {getLabDisplayName(transaction, 'labB')}
            </h4>
            <div className="space-y-2">
              <div>
                <p className="font-medium text-sm">المنتج: {transaction.parties?.labB?.product?.name}</p>
                <p className="text-xs text-gray-600">الكمية: {transaction.parties?.labB?.quantity}</p>
              </div>
            </div>
          </div>
        </div>

        {/* معلومات الرسوم فقط */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="text-sm text-center">
            <p className="text-gray-600">رسوم التبادل:</p>
            <p className="font-medium text-blue-600 text-lg">{transaction.financial?.feeAmount?.toLocaleString()} ريال</p>
          </div>
        </div>

        {/* حالة الشحن - عرض فقط بدون أزرار */}
        {transaction.shipping && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {renderShippingCard(
              transaction, 
              'aToB', 
              `شحن: ${getLabDisplayName(transaction, 'labA')} → ${getLabDisplayName(transaction, 'labB')}`
            )}
            {renderShippingCard(
              transaction, 
              'bToA', 
              `شحن: ${getLabDisplayName(transaction, 'labB')} → ${getLabDisplayName(transaction, 'labA')}`
            )}
          </div>
        )}

        {/* الأزرار - عرض التفاصيل فقط */}
        <div className="flex gap-3">
          <button
            onClick={() => handleViewDetails(transaction._id)}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
          >
            <EyeIcon className="w-4 h-4 ml-1" />
            عرض التفاصيل
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 ml-1" />
              {new Date(transaction.createdAt).toLocaleDateString('ar-SA')}
            </div>
            <div className="flex items-center">
              <CurrencyDollarIcon className="w-4 h-4 ml-1" />
              رسوم: {transaction.financial?.feeAmount?.toLocaleString()} ريال
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (loading) return <LoadingScreen />;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ArrowsRightLeftIcon className="h-8 w-8 ml-3 text-blue-600" />
                التبادلات النشطة
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                متابعة حالة التبادلات والشحنات الخاصة بك
              </p>
            </div>
            <button
              onClick={loadTransactions}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <ArrowPathIcon className="h-5 w-5 ml-2" />
              تحديث
            </button>
          </div>
        </div>
      </div>

      {/* الفلاتر */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">جميع الحالات</option>
            <option value="confirmed">مؤكد</option>
            <option value="shipping_a_to_b">شحن A→B</option>
            <option value="shipping_b_to_a">شحن B→A</option>
            <option value="both_shipping">شحن مزدوج</option>
            <option value="completed">مكتمل</option>
          </select>
          
          <select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-');
              setFilters(prev => ({ ...prev, sortBy, sortOrder }));
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="createdAt-desc">الأحدث أولاً</option>
            <option value="createdAt-asc">الأقدم أولاً</option>
            <option value="totalValue-desc">الأعلى قيمة</option>
            <option value="totalValue-asc">الأقل قيمة</option>
          </select>
        </div>
      </div>

      {/* قائمة التبادلات */}
      <div className="grid grid-cols-1 gap-6">
        {transactions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <ArrowsRightLeftIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد تبادلات نشطة</h3>
            <p className="text-gray-500">لم تقم بأي تبادلات بعد</p>
          </div>
        ) : (
          transactions.map(renderTransactionCard)
        )}
      </div>

      {/* نموذج التفاصيل */}
      <AnimatePresence>
        {showDetailsModal && selectedTransaction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center">
                      <ArrowsRightLeftIconSolid className="h-6 w-6 ml-2" />
                      تفاصيل معاملة التبادل
                    </h2>
                    <p className="text-blue-100 mt-1">#{selectedTransaction.transactionNumber}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* الجدول الزمني */}
                {selectedTransaction.timeline && selectedTransaction.timeline.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 ml-2 text-blue-600" />
                      الجدول الزمني
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-2">
                        {selectedTransaction.timeline.map((event, index) => (
                          <div key={index} className="flex items-center justify-between p-2">
                            <div className="flex items-center space-x-2 space-x-reverse">
                              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                              <span className="text-sm font-medium">{event.description}</span>
                            </div>
                            <span className="text-sm text-gray-500">
                              {new Date(event.timestamp).toLocaleString('ar-SA')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* تفاصيل الشحن */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {renderShippingCard(
                    selectedTransaction, 
                    'aToB', 
                    'الشحنة الأولى (A→B)'
                  )}
                  {renderShippingCard(
                    selectedTransaction, 
                    'bToA', 
                    'الشحنة الثانية (B→A)'
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActiveExchanges;